import React, { useEffect, useState, useCallback } from 'react';
import { Icon } from '@iconify/react/dist/iconify.js';
import { Avatar, Dialog } from '@mui/material';
import Pagination from '../components/main/Pagination';
import AddAdminDialog from '../components/modals/AddAdminDialog';
import { NavLink } from 'react-router-dom';
import { toast } from 'react-toastify';
import useAdminsApi from '../api/useAdminsApi';
import CommonLoader from '../components/loaders/CommonLoader';
import { debounce } from '../helper/debounce';
import { MdClose } from 'react-icons/md';
import useRoleApi from '../api/useRoleApi';

import { useAuth } from '../contexts/AuthContext';

const AdminListPage = () => {
    const { addAdmin, getAdmins, loading, updateAdmin, logoutUserBySuperAdmin } = useAdminsApi();
    const { profile } = useAuth();
    const [data, setData] = useState([]);
    const [openAdd, setOpenAdd] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(1);
    const [total_pages, setTotalPages] = useState(1);
    const [openEdit, setOpenEdit] = useState(false)
    const { getRoles, loading: roleLoading } = useRoleApi();
    const [roles, setRoles] = useState([]);
    const [active, setActive] = useState({});

    const fetchAdmins = async (page = 1, search = '') => {
        const res = await getAdmins(page, search);
        setData(res.data.payload.all_imp_users);
        setTotalPages(res.data.payload.total_pages);
    };
    const handleOpenEdit = (user) => {
        setActive(user);
        setOpenEdit(true)
    }

    const debouncedFetchAdmins = useCallback(
        debounce((value) => {
            fetchAdmins(1, value);
            setPage(1);
        }, 500),
        []
    );

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        debouncedFetchAdmins(value);
    };

    const handleAddAdmin = async (e, formData) => {
        e.preventDefault();
        if (!formData.email || !formData.role_id) {
            toast.error('Please fill all fields');
            return;
        }
        await addAdmin(formData);
        fetchAdmins(page, searchTerm); // reload current page
        setOpenAdd(false);
        toast.success('Admin Added Successfully');
    };

    const handlePageChange = (newPage) => {
        setPage(newPage);
    };

    useEffect(() => {
        fetchAdmins(page, searchTerm);
    }, [page]);

    useEffect(() => {
        fetchRoles();
    }, []);

    const fetchRoles = async () => {
        try {
            const response = await getRoles(true);
            const activeRoles = (response.data?.data || []).filter(r => r.is_live);
            setRoles(activeRoles || []);
        } catch (err) {
            console.error(err);
        } finally {
        }
    };
    const handleLogoutBySuperAdmin = async (user) => {
        if (window.confirm(`Are you sure you want to logout ${user.user_name}?`)) {
            try {
                await logoutUserBySuperAdmin(user._id);
                toast.success(`${user.user_name} logged out successfully`);
                fetchAdmins(page, searchTerm);
            } catch (error) {
                console.error(error);
                toast.error("Failed to logout user");
            }
        }
    };
    const handleEditAdminSubmit = async (e) => {
        e.preventDefault();

        try {
            const payload = {
                id: active._id,
                email: active.email,
                user_name: active.user_name,
                phone_number: active.phone_number,
                role_id: active.role_id._id,
            };

            const salaryInfo = active.walletId?.salaryBaseInfo;

            if (salaryInfo?.salaryType) {
                payload.salaryType = salaryInfo.salaryType;
            }
            if (salaryInfo?.hourlyRate) {
                payload.hourlyRate = parseFloat(salaryInfo.hourlyRate);
            }
            if (salaryInfo?.monthlySalary) {
                payload.monthlySalary = parseFloat(salaryInfo.monthlySalary);
            }

            setOpenEdit(false);
            await updateAdmin(payload);
            toast.success('Admin updated successfully');
            fetchAdmins(page, searchTerm);
        } catch (error) {
            console.error(error);
            toast.error('Failed to update admin');
        }

    };

    return (
        <>
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-24">
                <h6 className="fw-semibold mb-0">Admins</h6>
                <button
                    type="button"
                    className="btn btn-primary text-sm btn-sm px-12 py-12 radius-8 d-flex align-items-center gap-2"
                    onClick={() => setOpenAdd(true)}
                >
                    <Icon icon="ic:baseline-plus" className="icon text-xl line-height-1" />
                    Add
                </button>
            </div>

            <div className="card h-100 p-0 radius-12">
                <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
                    <form className="navbar-search d-flex align-items-center gap-2">
                        <input
                            type="text"
                            className="form-control bg-base h-40-px w-auto"
                            name="search"
                            placeholder="Search"
                            value={searchTerm}
                            onChange={handleSearchChange}
                        />
                        <Icon icon="ion:search-outline" className="icon" />
                    </form>
                </div>

                <div className="card-body p-24">
                    {loading ? (
                        <CommonLoader />
                    ) : (
                        <>
                            <div className="table-responsive scroll-sm">
                                <table className="table bordered-table sm-table mb-0">
                                    <thead>
                                        <tr>
                                            <th className="text-start">Name</th>
                                            <th className="text-center">Email</th>
                                            <th className="text-center">Login Type</th>
                                            <th className="text-center">Role</th>
                                            <th className="text-center">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.length > 0 ? (
                                            data.map((user) => (
                                                <tr key={user.id}>
                                                    <td>
                                                        <div className="d-flex align-items-center">
                                                            <Avatar
                                                                src={user.user_id?.profile_photo}
                                                                alt=""
                                                                className="w-40-px h-40-px rounded-circle me-12"
                                                            />
                                                            <span className="text-md text-secondary-light">{user.user_name}</span>
                                                        </div>
                                                    </td>
                                                    <td className="text-center">{user.email}</td>
                                                    <td className="text-center">{user.login_type}</td>
                                                    <td className="text-center">
                                                        <span className="badge rounded-pill text-bg-primary">{user.role_id?.role}</span>
                                                    </td>
                                                    <td className="text-center">
                                                        <div className="d-flex justify-content-center gap-2">
                                                            <button
                                                                onClick={() => handleOpenEdit(user)}
                                                                type="button"
                                                                className="bg-success-focus text-success-600 bg-hover-success-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                                            >
                                                                <Icon icon="lucide:edit" className="menu-icon" />
                                                            </button>
                                                            {profile?.user_type === 'superadmin' && (
                                                                <button
                                                                    onClick={() => handleLogoutBySuperAdmin(user)}
                                                                    type="button"
                                                                    className="bg-danger-focus text-danger-600 bg-hover-danger-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                                                    title="Force Logout"
                                                                >
                                                                    <Icon icon="tabler:logout" className="menu-icon" />
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="5" className="text-center py-4">No Admins Found</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-24">
                                <span>Page {page} of {total_pages}</span>
                                <Pagination
                                    total_pages={total_pages}
                                    current_page={page}
                                    onPageChange={handlePageChange}
                                />
                            </div>
                        </>
                    )}
                </div>
            </div>

            <AddAdminDialog open={openAdd} onClose={() => setOpenAdd(false)} handleAddAdmin={handleAddAdmin} />
            <Dialog
                open={openEdit}
                onClose={() => setOpenEdit(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: '16px' } }}
            >
                <div className="card-body">

                    <form onSubmit={handleEditAdminSubmit}>
                        <div className="card mb-0">
                            <div className="card-header d-flex justify-content-between align-items-center">
                                <h6 className="card-title mb-0">Edit Admin</h6>
                                <button type="button" className="btn btn-sm btn-light p-1" onClick={() => setOpenEdit(false)}>
                                    <MdClose size={18} />
                                </button>
                            </div>

                            <div className="card-body">
                                <div className="row gy-3">
                                    <div className="col-md-6">
                                        <label className="form-label">Email</label>
                                        <input
                                            type="email"
                                            className="form-control"
                                            placeholder="Enter Email"
                                            value={active?.email || ''}
                                            onChange={(e) => setActive(prev => ({ ...prev, email: e.target.value }))}
                                            required
                                        />
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label">Role</label>
                                        <select
                                            className="form-select"
                                            required
                                            value={active?.role_id?._id || ''}
                                            onChange={(e) => {
                                                const selectedRole = roles.find(role => role._id === e.target.value);
                                                setActive(prev => ({
                                                    ...prev,
                                                    role_id: selectedRole || {}  // set full object, not just _id
                                                }));
                                            }}
                                        >
                                            <option value="">Select Role</option>
                                            {roles?.map((r) => (
                                                <option key={r._id} value={r._id}>
                                                    {r.role}
                                                </option>
                                            ))}
                                        </select>

                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label">User Name</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Enter User Name"
                                            value={active?.user_name || ''}
                                            onChange={(e) => setActive(prev => ({ ...prev, user_name: e.target.value }))}
                                            required
                                        />
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label">Phone Number</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Enter Phone Number"
                                            value={active?.phone_number || ''}
                                            onChange={(e) => setActive(prev => ({ ...prev, phone_number: e.target.value }))}
                                            maxLength={10}
                                        />
                                    </div>

                                    {/* <div className="col-md-6">
                                        <label className="form-label">Hourly Rate</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Enter Hourly Rate"
                                            value={active?.walletId?.salaryBaseInfo?.hourlyRate || ''}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                setActive(prev => ({
                                                    ...prev,
                                                    walletId: {
                                                        ...prev.walletId,
                                                        salaryBaseInfo: {
                                                            ...prev.walletId?.salaryBaseInfo,
                                                            hourlyRate: value
                                                        }
                                                    }
                                                }));
                                            }}
                                            required={active?.role_id?.role?.toLowerCase() !== 'user'}

                                        />
                                    </div> */}

                                    {/* <div className="col-md-6">
                                        <label className="form-label">Monthly Salary</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Enter Monthly Salary"
                                            value={active?.walletId?.salaryBaseInfo?.monthlySalary || ''}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                setActive(prev => ({
                                                    ...prev,
                                                    walletId: {
                                                        ...prev.walletId,
                                                        salaryBaseInfo: {
                                                            ...prev.walletId?.salaryBaseInfo,
                                                            monthlySalary: value
                                                        }
                                                    }
                                                }));
                                            }}
                                            required={active?.role_id?.role?.toLowerCase() !== 'user'}
                                        />
                                    </div> */}

                                    {/* <div className="col-md-6">
                                        <label className="form-label">Salary Type</label>
                                        <select
                                            className="form-select"
                                            required={active?.role_id?.role?.toLowerCase() !== 'user'}
                                            value={active?.walletId?.salaryBaseInfo?.salaryType || ''}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                setActive(prev => ({
                                                    ...prev,
                                                    walletId: {
                                                        ...prev.walletId,
                                                        salaryBaseInfo: {
                                                            ...prev.walletId?.salaryBaseInfo,
                                                            salaryType: value
                                                        }
                                                    }
                                                }));
                                            }}
                                        >
                                            <option value="">Select salary type</option>
                                            <option value="fixed">Fixed</option>
                                            <option value="incentive">Incentive</option>
                                            <option value="fixed_plus_incentive">Fixed Plus Incentive</option>
                                        </select>
                                    </div> */}

                                    <div className="col-12 text-end">
                                        <button type="button" className="btn btn-secondary me-2" onClick={() => setOpenEdit(false)}>
                                            Cancel
                                        </button>
                                        <button type="submit" className="btn btn-primary">
                                            Save
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </form>


                </div>
            </Dialog>
        </>
    );
};

export default AdminListPage;
